import type { NumberedSectionId } from "./section-order"

export const CRISIS_CARDS = [
  {
    icon: `chart` as const,
    kicker: `Demand`,
    head: `Skyrocketing`,
    tint: `accent` as const,
    body: `Peak demand is expected to double over the next decade. Home electrification — EVs, heat pumps, appliances — keeps adding to the strain.`,
  },
  {
    icon: `building` as const,
    kicker: `Infrastructure`,
    head: `Failing`,
    tint: `gold` as const,
    body: `A grid built 50+ years ago for different needs struggles with today's load. Aging transmission lines and overloaded substations fail more often.`,
  },
  {
    icon: `warning` as const,
    kicker: `Outages`,
    head: `Increasing`,
    tint: `danger` as const,
    body: `Power disruptions have doubled in the last decade from extreme weather and utility shutoffs. PSPS events proactively cut power to whole regions.`,
  },
]

// Outage timeline. Figures rounded from public sources (2026-08): food safety
// windows — foodsafety.gov; restock and hotel costs — Generac, EcoFlow, STR;
// water/pipe damage — Generac, Kohler; 11-hour average — EIA (2024).
export const OUTAGE_TIMELINE = [
  {
    at: `Hour 0`,
    head: `Lights out`,
    body: `Wi-Fi drops. Work stops. The sump pump and furnace go quiet with everything else.`,
    cost: null,
    tint: `text` as const,
  },
  {
    at: `Hour 4`,
    head: `The fridge clock runs out`,
    body: `Food in the refrigerator is no longer safe to keep. Phones and laptops are running on whatever charge they had.`,
    cost: `$250 in groceries`,
    tint: `accent` as const,
  },
  {
    at: `Hour 24`,
    head: `Decisions get expensive`,
    body: `A hotel for the night. Meals out. Anyone on a CPAP, oxygen, or refrigerated medication needs a plan tonight.`,
    cost: `$150 a night, $200 in lost work`,
    tint: `gold` as const,
  },
  {
    at: `Hour 48`,
    head: `The house is at risk`,
    body: `A full freezer thaws. A wet basement or a cold snap turns an inconvenience into an insurance claim.`,
    cost: `$500 freezer, $2,000–$10,000+ water or pipes`,
    tint: `danger` as const,
  },
] as const

export const BATTERY_POINTS = [
  [`Completely silent`, `No noise pollution`],
  [`Zero maintenance`, `Set and forget`],
  [`Instant power`, `0.1-second switching`],
  [`Clean energy`, `Zero emissions`],
  [`10-year warranty`, `Comprehensive coverage`],
] as const

export const GENERATOR_POINTS = [
  [`Loud operation`, `Noise pollution`],
  [`High maintenance`, `Constant upkeep`],
  [`Fuel costs`, `Ongoing expense`],
  [`Carbon emissions`, `Environmental impact`],
  [`Weather exposure`, `Wear and tear`],
] as const

export const BACKUP_MODES = [
  {
    title: `Essential systems`,
    icon: `shield` as const,
    runtime: `2–3 days`,
    tint: `success` as const,
    items: [
      `Home office`,
      `Security systems`,
      `Refrigeration`,
      `Critical circuits`,
    ],
  },
  {
    title: `Whole-home mode`,
    icon: `home` as const,
    runtime: `8–12 hours`,
    tint: `accent` as const,
    items: [`HVAC`, `EV charging`, `Appliances`, `All outlets`],
  },
]

export const CARE_BENEFITS = [
  {
    title: `Parts & labor covered 100%`,
    note: `End-to-end coverage on hardware and onsite service.`,
  },
  { title: `$0 deductible`, note: `Never a fee to file a claim.` },
  {
    title: `Complete U.S. coverage`,
    note: `Works across all 50 states.`,
  },
  {
    title: `Stays with the home`,
    note: `Transfers to new homeowner if property is sold.`,
  },
  {
    title: `One battery replacement`,
    note: `If capacity drops below 50% after the manufacturer's warranty ends, we replace it.`,
  },
  {
    title: `Claims handled in-house`,
    note: `No third-party insurance. Powur manages everything.`,
  },
] as const

export const CARE_COVERAGE = [
  { label: `Battery modules`, icon: `battery` as const },
  { label: `Inverter & charger`, icon: `bolt` as const },
  { label: `Battery management`, icon: `shield` as const },
  { label: `Disconnects`, icon: `plug` as const },
  { label: `Installation`, icon: `check` as const },
  { label: `24/7 Support`, icon: `help` as const },
]

export const CARE_BRANDS = [
  `Tesla`,
  `Enphase`,
  `FranklinWH`,
  `LG`,
  `Generac`,
] as const

export const NEXT_STEPS = [
  {
    day: `Today`,
    title: `Project Kick-off`,
    body: `We assign your installer automatically on day one, so work starts immediately.`,
  },
  {
    day: `Week 1`,
    title: `Site Survey`,
    body: `A short in-person visit to find the best spot for your battery and confirm your electrical setup.`,
  },
  {
    day: `2–3 weeks`,
    title: `Permitting`,
    body: `We handle the utility filing and city permits for you.`,
  },
  {
    day: `1–2 weeks`,
    title: `Installation & Inspection`,
    body: `Your battery is installed in a single day, then final inspection sign-off.`,
  },
  {
    day: `Day one`,
    title: `Battery On`,
    body: `Your battery goes live and starts shifting energy off peak rates.`,
  },
] as const

/** Every numbered section in rail order, labeled for the side navigation.
 * `savings` and `powur-care` are conditional — filter with the flags the
 * view derives from the saved run and warranty snapshot. */
export const SECTION_LINKS: readonly {
  id: NumberedSectionId
  label: string
}[] = [
  { id: `why-now`, label: `Why now` },
  { id: `hidden-cost`, label: `The hidden cost` },
  { id: `response`, label: `The smart response` },
  { id: `specs`, label: `Your system` },
  { id: `savings`, label: `Savings` },
  { id: `backup`, label: `Backup power` },
  { id: `powur-care`, label: `Powur Care` },
  { id: `ai-compute`, label: `Compute node` },
  { id: `investment`, label: `Investment` },
  { id: `next`, label: `Next steps` },
  { id: `homeowner-app`, label: `Homeowner app` },
]
